import { ImagePlus, X } from '@/components/ui/icons';
import { useEffect, useRef, useState, type ClipboardEvent, type FormEvent } from 'react';
import { APP_NAME, type FeedbackKind } from '@clanfire/shared';
import { feedbackApi } from '@/api/endpoints';
import { Button } from '@/components/ui/Button';
import { TextArea } from '@/components/ui/Input';
import { Modal } from '@/components/ui/Modal';
import { useSubmit } from '@/hooks/useForm';
import { cn } from '@/lib/utils';
import { toast } from '@/stores/ui';

const KINDS: Array<{ id: FeedbackKind; emoji: string; label: string; placeholder: string }> = [
  { id: 'bug', emoji: '🐞', label: 'Something’s broken', placeholder: 'What happened, and what did you expect? Steps to make it happen again help a lot.' },
  { id: 'idea', emoji: '💡', label: 'I have an idea', placeholder: 'What would make it better for you?' },
  { id: 'other', emoji: '💬', label: 'Something else', placeholder: 'Anything you’d like the team to know.' },
];

const MAX_SCREENSHOT = 8 * 1024 * 1024;

/** The Feedback button: goes straight to the team (with a screenshot, if you add one). */
export function FeedbackModal({ onClose }: { onClose: () => void }) {
  const [kind, setKind] = useState<FeedbackKind>('bug');
  const [message, setMessage] = useState('');
  const [screenshot, setScreenshot] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const fileInput = useRef<HTMLInputElement>(null);
  const { loading, error, setError, run } = useSubmit();
  const page = window.location.pathname;
  const current = KINDS.find((k) => k.id === kind)!;

  useEffect(() => {
    if (!screenshot) return setPreview(null);
    const url = URL.createObjectURL(screenshot);
    setPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [screenshot]);

  const pick = (file: File | undefined | null) => {
    if (!file) return;
    if (!file.type.startsWith('image/')) return setError('Screenshots have to be an image (PNG, JPEG, GIF or WebP).');
    if (file.size > MAX_SCREENSHOT) return setError('That screenshot is too big (8 MB at most).');
    setError(null);
    setScreenshot(file);
  };

  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const image = [...e.clipboardData.files].find((f) => f.type.startsWith('image/'));
    if (image) {
      e.preventDefault();
      pick(image);
    }
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (message.trim().length < 5) return setError('Tell us a little more (at least 5 characters).');
    const ok = await run(() => feedbackApi.send({ kind, message: message.trim(), page, screen: `${window.screen.width}×${window.screen.height}`, screenshot }));
    if (!ok) return;
    toast.success('Thank you! That went straight to the team.');
    onClose();
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Send feedback"
      description={`Bugs, ideas, anything: it goes straight to the ${APP_NAME} team.`}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            Cancel
          </Button>
          <Button type="submit" form="feedback-form" loading={loading}>
            Send
          </Button>
        </>
      }
    >
      <form id="feedback-form" onSubmit={(e) => void submit(e)} className="flex flex-col gap-4">
        <div role="radiogroup" aria-label="What kind of feedback" className="grid grid-cols-3 gap-2">
          {KINDS.map((k) => (
            <label
              key={k.id}
              className={cn(
                'flex cursor-pointer flex-col items-center gap-1 rounded-xl border px-2 py-2.5 text-center text-xs font-semibold transition-colors has-[:focus-visible]:ring-2 has-[:focus-visible]:ring-accent/40',
                kind === k.id ? 'border-accent bg-accent-soft text-fg' : 'border-line text-fg-soft hover:bg-hover',
              )}
            >
              <input type="radio" name="feedback-kind" checked={kind === k.id} onChange={() => setKind(k.id)} className="sr-only" />
              <span className="text-xl" aria-hidden>
                {k.emoji}
              </span>
              {k.label}
            </label>
          ))}
        </div>
        <TextArea
          label="Tell us about it"
          data-autofocus
          rows={5}
          maxLength={2000}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onPaste={onPaste}
          placeholder={current.placeholder}
          hint={`${message.length.toLocaleString()} / 2,000 · you can paste a screenshot here too`}
          error={error ?? undefined}
        />
        <div>
          {preview ? (
            <div className="relative w-fit">
              <img src={preview} alt="Your screenshot" className="max-h-40 rounded-xl border border-line object-contain" />
              <button
                type="button"
                onClick={() => setScreenshot(null)}
                aria-label="Remove the screenshot"
                className="absolute -top-2 -right-2 rounded-full bg-elevated p-1 text-fg-muted shadow-pop hover:text-fg"
              >
                <X size={14} />
              </button>
            </div>
          ) : (
            <Button variant="secondary" size="sm" icon={<ImagePlus size={15} />} onClick={() => fileInput.current?.click()}>
              Add a screenshot
            </Button>
          )}
          <input
            ref={fileInput}
            type="file"
            accept="image/png,image/jpeg,image/gif,image/webp"
            hidden
            onChange={(e) => {
              pick(e.target.files?.[0]);
              e.target.value = '';
            }}
          />
        </div>
        <p className="text-xs text-fg-muted">
          We’ll include your username, the page you’re on (<code className="rounded bg-sunken px-1">{page}</code>), your browser and your screen size, so we can
          look into it.
        </p>
      </form>
    </Modal>
  );
}
